import { useState ,useEffect ,useRef } from "react";
import { Mic, MicOff, Send, Plus, Settings, Volume2,Trash2,Play,Pause,ImageIcon,X} from "lucide-react";
import {Dialog,DialogContent , DialogTitle
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent } from "./ui/tabs";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Separator } from "./ui/separator";
import { ChatRepository } from "@/infrastructure/repositories/ChatRepository";
import { Coach } from '../types/index';
import { ParsedAIResponse } from '../types/index';
import { PracticeRepository } from "@/infrastructure/repositories/PracticeRepository";
import { Practice } from "@/domain/entities/Practice";

const chatRepo = new ChatRepository();

interface AIAgent {
  id: string;
  name: string;
  description: string;
  avatar: string;
  knowledgeFiles: number;
  status: "active" | "training" | "inactive";
  specialty: string;
}

interface Message {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  isVoice?: boolean;
  metadata?: any;
  image?: string;
  imageFile?: File;
}

const mockAgents: AIAgent[] = [
  {
    id: "1",
    name: "Coach Alex",
    description: "Specialized in freestyle and backstroke techniques",
    avatar: "CA",
    knowledgeFiles: 12,
    status: "active",
    specialty: "Stroke Technique"
  },
  {
    id: "2", 
    name: "Training Bot",
    description: "Expert in workout planning and endurance training",
    avatar: "TB",
    knowledgeFiles: 8,
    status: "training",
    specialty: "Training Plans"
  }
];

export function AIAssistanceContent() {

const userDataString = localStorage.getItem("user_data");
const userData = userDataString ? JSON.parse(userDataString) : null;
const userType = userData?.type || "swimmer"; 

const [dialogOpen, setDialogOpen] = useState(false);
const [dialogStatus, setDialogStatus] = useState<"success" | "error" | "loading" | null>(null);
const [dialogMessage, setDialogMessage] = useState("");
const [dialogMessageTitle, setDialogMessageTitle] = useState("");
const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
const [isChatDelete, setIsChatDelete] = useState(false);

const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
console.log(documentToDelete);
const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
const [isRecording, setIsRecording] = useState(false);
const [isPlaying, setIsPlaying] = useState(false);
const [message, setMessage] = useState("");
const [messages, setMessages] = useState<Message[]>([]);
const [page, setPage] = useState(1);
const [hasMore, setHasMore] = useState(true);
const [isLoadingMore, setIsLoadingMore] = useState(false);
const [isSending, setIsSending] = useState(false);
const [practiceDialogOpen, setPracticeDialogOpen] = useState(false);
const [practiceToAdd, setPracticeToAdd] = useState<any[]>([]);
const [practiceName, setPracticeName] = useState("");
const [practiceDescription, setPracticeDescription] = useState("");
const [isCoachLoading, setIsCoachLoading] = useState(true);

//const repo = new SharetribeRepository(apiService);

// const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
// const [paymentMethodId, setPaymentMethodId] = useState("");
// console.log(setPaymentMethodId);  
 const userId = localStorage.getItem("current_user_swimmer_id") || "";

// const checkSubscription = async () => {
//   try {
//     const res = await repo.sharedTribecurrentUserShow(false, false, true);

//     const publicData = res?.data?.attributes?.profile?.publicData || {};

//     const proId = publicData.coach_pro_product_id;
//     const proReceipt = publicData.coach_pro_product_id_receipt;

//     const isActive = Boolean(
//       (proId && proId.trim() !== "") ||
//       (proReceipt && proReceipt.trim() !== "")
//     );

//     console.log("Subscription active:", isActive);

//     setHasSubscription(isActive);
//   } catch (err) {
//     console.error("subscription error:", err);
//     setHasSubscription(false);
//   }
// };

// useEffect(() => {
//   checkSubscription();
// }, []);

const isJsonLike = (text: string) => {
  const t = text.trim();
  return (
    (t.startsWith("{") && t.endsWith("}")) ||
    (t.startsWith("[") && t.endsWith("]"))
  );
};

const extractMsgFromJsonLike = (text: string): string | null => {
  const match = text.match(/['"]msg['"]\s*:\s*['"]([^'"]+)['"]/i);
  return match ? match[1] : null;
};

const messagesEndRef = useRef<HTMLDivElement | null>(null);
const chatContainerRef = useRef<HTMLDivElement | null>(null);
  // Scroll to bottom whenever new message is added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const resolveAIText = (
  ai: ParsedAIResponse | null | undefined,
  fallback: string
): string => {
  if (!ai) return fallback;

  const original = ai.original_response?.trim();

  if (original && !isJsonLike(original)) {
    return original;
  }

  if (original && isJsonLike(original)) {
    const extracted = extractMsgFromJsonLike(original);
    if (extracted) return extracted;
  }

  if (ai.msg && ai.msg !== "Parse error") {
    return ai.msg;
  }

  return fallback;
};
const fetchChats = async (
  pageNumber: number,
  userId: string,
  coachId: string | null
) => {
  try {
    setIsLoadingMore(true);

    const container = chatContainerRef.current;
    const previousScrollHeight = container?.scrollHeight || 0;

    const chats = await chatRepo.fetchChatsWithAI(
      userId,        // always swimmer
      pageNumber,
      10,
      coachId        // selected coach id
    );

    if (!chats.length) {
      setHasMore(false);
      return;
    }

    const formatted: Message[] = chats.map((chat) => {
      const isAssistant = chat.isFromAI();
      const hasImage = chat.hasImage();

      const shouldHideText =
        !isAssistant &&
        hasImage &&
        chat.message.trim().toLowerCase() === "image related question";

      const content = isAssistant
        ? resolveAIText(chat.metadata, chat.message)
        : shouldHideText
          ? ""
          : chat.message;

      return {
        id: chat.id,
        type: isAssistant ? "assistant" : "user",
        content,
        timestamp: new Date(chat.timestamp), 
        metadata: chat.metadata,
        image: hasImage
          ? `https://swimnerd-ai.azurewebsites.net/${chat.filePath}`
          : undefined,
      };
    });

    formatted.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    if (pageNumber > 1) {
      setMessages((prev) => [...formatted, ...prev]);
      setTimeout(() => {
        if (container) {
          container.scrollTop =
            container.scrollHeight - previousScrollHeight;
        }
      }, 100);
    } else {
      setMessages(formatted);
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  } catch (err) {
    console.error("Error fetching chats:", err);
  } finally {
    setIsLoadingMore(false);
  }
};


const handleLoadMore = async () => {
  if (isLoadingMore || !hasMore) return;

  const nextPage = page + 1;
  const coachId = selectedCoach?.id ?? null;

  await fetchChats(nextPage, userId, coachId);
  setPage(nextPage);
};


  // Send Message Function
const [selectedImage, setSelectedImage] = useState<File | null>(null);


const handleSendMessage = async () => {
  if ((!message.trim() && !selectedImage) || (userType === "swimmer" && !selectedCoach)) return;

  const newMessage: Message = {
    id: Date.now().toString(),
    type: "user",
    content: message,
    timestamp: new Date(),
    image: selectedImage ? URL.createObjectURL(selectedImage) : undefined,
    imageFile: selectedImage ?? undefined,
  };

  setMessages((prev) => [...prev, newMessage]);
  setMessage("");
  setSelectedImage(null);
  setIsSending(true);

  try {
    // Determine user and coach roles
    let receiverUserId = userId;
    let senderCoachId: string | null = null;
    let isCoachRequest = false;

    if (userType === "swimmer") {
      receiverUserId = userId;
      senderCoachId =
        selectedCoach?.id && selectedCoach.id !== userId ? selectedCoach.id : null;
      isCoachRequest = false;
    } else if (userType === "coach") {
      receiverUserId = userId;
      senderCoachId = null;
      isCoachRequest = true;
    }

    //  Ensure all params are strings — no nulls
    const coachIdToSend = senderCoachId ?? "";

    //  Call API with optional image
    const { aiContent, parsedAI } = await chatRepo.sendMessageToAI(
      message,
      receiverUserId,
      coachIdToSend, 
      isCoachRequest,
      selectedImage ?? undefined 
    );

    // Handle AI response
    const ai: ParsedAIResponse | null =
      parsedAI && typeof parsedAI === "object" ? (parsedAI as ParsedAIResponse) : null;

    let displayContent: string;

    if (ai) {
      // Prioritize msg field when it exists (especially for error cases with status 0)
      const original = ai.original_response?.trim();

  // Case 1: Human-readable original response
  if (original && !isJsonLike(original)) {
    displayContent = original;
  }
  // Case 2: JSON-like original response → extract msg
  else if (original && isJsonLike(original)) {
    const extractedMsg = extractMsgFromJsonLike(original);
    if (extractedMsg) {
      displayContent = extractedMsg;
    } else if (ai.msg && ai.msg !== "Parse error") {
      displayContent = ai.msg;
    } else {
      displayContent = aiContent;
    }
  }
  // Case 3: Normal msg (non-parse-error)
  else if (ai.msg && ai.msg !== "Parse error") {
    displayContent = ai.msg;
  }
  else {
    displayContent = aiContent;
  }
} else {
  displayContent = aiContent;
}
    const responseMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: "assistant",
      content: displayContent,
      timestamp: new Date(),
      metadata: ai,
    };

    setMessages((prev) => [...prev, responseMessage]);
  } catch (error) {
    console.error("Error sending message to AI:", error);
    const errorMessage: Message = {
      id: (Date.now() + 2).toString(),
      type: "assistant",
      content: "Something went wrong. Please try again later.",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, errorMessage]);
  } finally {
    setIsSending(false);
  }
};


const toggleRecording = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      // Simulate recording for 3 seconds
      setTimeout(() => {
        setIsRecording(false);
        const voiceMessage: Message = {
          id: Date.now().toString(),
          type: "user", 
          content: "Voice message: How can I improve my freestyle breathing technique?",
          timestamp: new Date(),
          isVoice: true
        };
        setMessages(prev => [...prev, voiceMessage]);
      }, 3000);
    }
  };

const [newAgentName, setNewAgentName] = useState("");
const [newAgentDescription, setNewAgentDescription] = useState("");
const createNewAgent = () => {
  if (!newAgentName.trim()) return;
  
  // Here you would typically call an API to create the agent
  setNewAgentName("");
  setNewAgentDescription("");
};


const confirmDelete = async () => {
  setDeleteConfirmOpen(false);
  setDialogStatus("loading");
  setDialogMessageTitle(isChatDelete ? "Clearing Chat..." : "Deleting...");
  setDialogMessage(
    isChatDelete ? "Removing all chat messages..." : "Deleting your document..."
  );
  setDialogOpen(true);

  try {
    if (isChatDelete) {
      try {
        await chatRepo.deleteAIAllMessages(
          userId,
          userType === "swimmer" && selectedCoach?.id ? selectedCoach.id : null
        );
        setMessages([]);
        setDialogStatus("success");
        setDialogMessageTitle("Chat Cleared");
        setDialogMessage("All chat messages have been successfully removed.");
      } catch (error) {
        console.error("Error clearing chat:", error);
        setDialogStatus("error");
        setDialogMessageTitle("Chat Clear Failed");
        setDialogMessage(`Failed to clear chat: ${(error as Error).message}`);
      }
    }
  } catch (error) {
    console.error("Delete error:", error);
    setDialogStatus("error");
    setDialogMessageTitle("Delete Failed");
    setDialogMessage(`Failed to delete: ${(error as Error).message}`);
  } finally {
    setDocumentToDelete(null);
    setIsChatDelete(false);
  }
};

const [coaches, setCoaches] = useState<Coach[]>([]);

useEffect(() => {
  const fetchCoachList = async () => {
    if (userType === "coach") {
      setIsCoachLoading(false);
      return;
    }

    try {
      setIsCoachLoading(true);

      const result = await chatRepo.fetchCoaches(userId);

      const selfData: Coach = {
        id: userId,
        name: userData?.name || "AI Assistant",
        avatar: userData?.avatar || "",
        lang: "Self",
        status: "active",
      };

      const allCoaches = [selfData, ...result];
      setCoaches(allCoaches);
      setSelectedCoach(selfData);
    } catch (error) {
      console.error("Error fetching coaches:", error);
    } finally {
      setIsCoachLoading(false);
    }
  };

  fetchCoachList();
}, [userId, userType]);



useEffect(() => {
  if (userType === "coach") {
    fetchChats(1, userId, null);
  }
}, [userType, userId]);

const handleClearChat = () => {
  setIsChatDelete(true);
  setDeleteConfirmOpen(true);
};
const handleAddPractice = async (practices: any[]) => {
  const userIdStr = localStorage.getItem("current_user_swimmer_id") || "";
  const swimmerId = Number(userIdStr) || 0;
  const repo = new PracticeRepository();

  setDialogStatus("loading");
  setDialogMessageTitle("Adding Practices...");
  setDialogMessage("Your practices are being added. Please wait...");
  setDialogOpen(true);

  try {
    let nextId = Math.max(0, ...practices.map(p => Number(p.id) || 0)) + 1;

    for (const p of practices) {
        const today = new Date();
      const newPracticeId = nextId.toString();
      nextId++;

      const practiceModel = Practice.fromData({
        id: newPracticeId,
        coach_id: swimmerId,
        swimmer_id: swimmerId,
        name: p.name || "AI Suggested Practice",
        description: p.description || "",
          practice_date: today.toISOString(), 
        practice_time: p.practice_time || "00:00:00",
        practice_CourseTypeLookupId: p.practice_CourseTypeLookupId || 0,
        isFavorite: 0,
        isFinished: false,
        imageData: p.imageData || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      const lapModels = (p.laps || []).map((lap: any, index: number) => ({
        practice_lap_id: 0, 
        rep: Number(lap.rep) || 1,
        distance: Number(lap.distance) || 25,
        practice_id: Number(newPracticeId),
        interval: (Number(lap.interval_lap) || 0), 
        energy_zone: lap.energy_zone || "Warm Up",
        description: lap.description || `Lap ${index + 1}`,
        time: 0,
        createdAtString: new Date().toISOString().slice(0, 19).replace("T", " "),
        createdAt: new Date().toISOString(),
        passedRepNumber: 0,
        isNoEditable: false,
        SyncStatus: 1,
      }));

      await repo.save(swimmerId, swimmerId, practiceModel, lapModels);
    }

    setDialogStatus("success");
    setDialogMessageTitle("Practice Added Successfully!");
    setDialogMessage("Your new practices were saved successfully.");
  } catch (error: any) {
    console.error("Error adding practice:", error);

    setDialogStatus("error");
    setDialogMessageTitle("Add Practice Failed");
    setDialogMessage(`Failed to add practice: ${error.message || "Unknown error"}`);
  }
};


// // -------------------------------------------------------
// // Subscription Gate BEFORE main return
// // ------------------------------ -------------------------
// if (hasSubscription === null) {
//   return (
//     <div className="p-6 text-center text-gray-600">
//       Checking subscription...
//     </div>
//   );
// }

// if (hasSubscription === false) {
//   return (
//     <div className="p-6 flex justify-center">
//       <SubscriptionProduct
//         paymentMethodId={paymentMethodId}
//         repo={repo}
//         onSuccess={() => {
//           setHasSubscription(true);
//         }}
//       />
//     </div>
//   );
// }

if (isCoachLoading) {
  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
    </div>
  );
}

  return (

    <div className="space-y-6">
      
      <div className="flex items-center justify-between">
        <div>
          <h1>AI Assistance</h1>
          <p className="text-muted-foreground">
            Create intelligent coaching agents and train with AI-powered assistance
          </p>
        </div>
        <Badge variant="secondary">Beta</Badge>
      </div>
      <Tabs defaultValue="chat" className="w-full">
       
   <TabsContent value="chat" className="space-y-4">
  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
{/* Active Agent Section (Visible to both swimmer & coach) */}
<div className="lg:col-span-1">
  <Card>
    <CardHeader>
      <CardTitle>Active Agents</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {/* Ask Me Option */}
      <div
        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
          selectedCoach?.id === "ask-me"
            ? "bg-primary/10 border-primary"
            : "hover:bg-muted"
        }`}
        onClick={() => {
          setSelectedCoach({
            id: "ask-me",
            name: "Ask Me",
            avatar: "",
            lang: "AI",
            status: "active"
          } as Coach);
          setMessages([]);
          setPage(1);
          setHasMore(true);
          fetchChats(1, userId, null);
        }}
      >
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback>AM</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">Ask Me</p>
            <p className="text-sm text-muted-foreground truncate">
              General questions and help
            </p>
          </div>
        </div>
      </div>

      {/* AI Assistant Option */}
      <div
        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
          selectedCoach?.id === "ai-assistant"
            ? "bg-primary/10 border-primary"
            : "hover:bg-muted"
        }`}
        onClick={() => {
          const coach = {
            id: "",
            name: "Swimnerd Ai Assistant",
            avatar: "",
            lang: "AI",
            status: "active"
          } as Coach;


          setSelectedCoach(coach);
          setMessages([]);
          setPage(1);
          setHasMore(true);

          fetchChats(1, userId, coach.id);
        }}


      >
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback>AI</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">Swimnerd Ai Assistant</p>
            <p className="text-sm text-muted-foreground truncate">
              Technical, stroke, and training guidance
            </p>
          </div>
        </div>
      </div>

      {/* Active Coaches — Swimmer only */}
      {userType === "swimmer" && (
        <>
          <Separator />
          <h4 className="text-sm font-semibold text-muted-foreground">
            Active Coaches
          </h4>

          {coaches.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center">
              No active coaches found.
            </p>
          ) : (
            coaches
              .filter((coach) => coach.id !== userId) // exclude self
              .map((coach) => (
                <div
                  key={coach.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedCoach?.id === coach.id
                      ? "bg-primary/10 border-primary"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => {
                    setSelectedCoach(coach);
                    setMessages([]);
                    setPage(1);
                    setHasMore(true);

                    fetchChats(1, userId, coach.id);
                  }}

                >
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-8 w-8">
                      {coach.avatar?.startsWith("http") ? (
                        <img
                          src={coach.avatar}
                          alt={coach.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <AvatarFallback>
                          {coach.avatar || coach.name?.[0] || "?"}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{coach.name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                       Ai Assistant
                      </p>
                    </div>
                    <Badge variant="default" className="h-5">
                      {coach.status}
                    </Badge>
                  </div>
                </div>
              ))
          )}
        </>
      )}
    </CardContent>
  </Card>
</div>

    {/* Chat Interface */}
    <div className="lg:col-span-3">

      <Card className="h-[600px] flex flex-col">
        {/* Chat Header */}
        <CardHeader className="flex-row items-center space-y-0 pb-2">
          {userType === "swimmer" && selectedCoach ? (
            <div className="flex items-center space-x-3">
              <Avatar>
                {selectedCoach?.avatar?.startsWith("http") ? (
                  <img
                    src={selectedCoach.avatar}
                    alt={selectedCoach.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <AvatarFallback>
                    {selectedCoach?.avatar || selectedCoach?.name?.[0] || "?"}
                  </AvatarFallback>
                )}
              </Avatar>

              <div>
                <CardTitle>{selectedCoach?.name}</CardTitle>
                <CardDescription>{selectedCoach?.email}</CardDescription>
              </div>
            </div>
          ) : (
            <CardTitle>Chat Assistant</CardTitle> // default header for coach
          )}

          <div className="ml-auto flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm">
              <Volume2 className="h-4 w-4" />
            </Button>

          {/* Clear Chat visible to both coach & swimmer */}
            <Button
              variant="destructive"
              size="icon"
              title="Clear Chat"
              onClick={handleClearChat}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <Separator />

        {/* Chat Content */}
        <CardContent className="flex flex-col flex-1 overflow-hidden p-0">
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {/* Load Older Messages */}
              {hasMore && messages.length > 10 && (
                <div className="text-center mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load older messages"}
                  </Button>
                </div>
              )}

              {/* Chat Messages */}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.type === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.type === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    {/* Voice Message (if any) */}
                    {msg.isVoice && (
                      <div className="flex items-center mb-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setIsPlaying(!isPlaying)}
                        >
                          {isPlaying ? (
                            <Pause className="h-3 w-3" />
                          ) : (
                            <Play className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Image Message */}
                    {msg.image && (
                      <img
                        src={msg.image}
                        alt="attached"
                        className="rounded-lg mb-2 max-w-[250px] max-h-[250px] object-cover"
                      />
                    )}

                    {/* Text Message */}
                    <div className="text-sm whitespace-pre-wrap">{msg.content}</div>

                    {/* Assistant-specific actions */}
                    {msg.type === "assistant" &&
                      msg.metadata?.data?.practices &&
                      Array.isArray(msg.metadata.data.practices) &&
                      msg.metadata.data.practices.length > 0 && (
                        <div className="mt-2 text-right">
                          <Button
                              size="sm"
                              onClick={() => {
                                const practices = msg.metadata.data.practices;
                                const laps = msg.metadata.data.laps;

                                if (practices && practices.length > 0) {
                                  const combined = practices.map((p: any) => ({
                                    ...p,
                                    laps: laps?.filter((l: any) => l.practice_id === p.practice_id) || []
                                  }));

                                  setPracticeToAdd(combined);
                                  setPracticeName(practices[0].name || "AI Suggested Practice");
                                  setPracticeDescription(practices[0].description || "");
                                  setPracticeDialogOpen(true);
                                }
                              }}
                            >
                              Add Practice
                            </Button>


                        </div>
                      )}
                    <p className="text-xs opacity-70 mt-1">
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}

              {/* AI Loader */}
              {isSending && (
                <div className="flex justify-start">
                  <div className="bg-muted text-sm text-gray-600 rounded-lg px-3 py-2">
                    Thinking...
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Voice Recording Indicator */}
          {isRecording && (
            <div className="p-4 bg-red-50 border-t">
              <div className="flex items-center justify-center space-x-2">
                <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-red-600">Recording... Speak now</span>
                <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          )}

          {/*Input Area */}
          <div className="p-4 border-t space-y-2">
            {/* Image Preview */}
            {selectedImage && (
              <div className="relative border rounded-lg p-2 bg-muted/50 inline-block">
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="preview"
                  className="max-h-40 rounded-md"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1"
                  onClick={() => setSelectedImage(null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}

            <div className="flex space-x-2 items-center">
              <Input
                placeholder="Type your message or use voice..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                className="flex-1"
              />

              {/* Hidden File Input */}
              <input
                type="file"
                accept="image/*"
                id="image-upload"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setSelectedImage(file);
                }}
              />

              {/* Upload Image Button */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => document.getElementById("image-upload")?.click()}
              >
                <ImageIcon className="h-4 w-4" />
              </Button>

              {/* Voice Button */}
              <Button
                variant={isRecording ? "destructive" : "outline"}
                size="icon"
                onClick={toggleRecording}
              >
                {isRecording ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>

              {/* Send Button */}
              <Button
                onClick={handleSendMessage}
                disabled={!message.trim() && !selectedImage}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
   </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Create New Agent */}
            <Card>
              <CardHeader>
                <CardTitle>Create New Agent</CardTitle>
                <CardDescription>
                  Build a custom AI assistant specialized for your coaching needs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block mb-2">Agent Name</label>
                  <Input
                    placeholder="e.g., Technique Coach"
                    value={newAgentName}
                    onChange={(e) => setNewAgentName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block mb-2">Description</label>
                  <Textarea
                    placeholder="Describe what this agent specializes in..."
                    value={newAgentDescription}
                    onChange={(e) => setNewAgentDescription(e.target.value)}
                    rows={3}
                  />
                </div>
                <Button onClick={createNewAgent} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Agent
                </Button>
              </CardContent>
            </Card>

            {/* Existing Agents */}
            <Card>
              <CardHeader>
                <CardTitle>Your Agents</CardTitle>
                <CardDescription>
                  Manage and configure your AI coaching assistants
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockAgents.map((agent) => (
                    <div key={agent.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Avatar>
                          <AvatarFallback>{agent.avatar}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{agent.name}</p>
                          <p className="text-sm text-muted-foreground">{agent.specialty}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline" className="h-5">
                              {agent.knowledgeFiles} files
                            </Badge>
                            <Badge 
                              variant={agent.status === 'active' ? 'default' : 'secondary'}
                              className="h-5"
                            >
                              {agent.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

          <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
            <DialogContent className="max-w-sm text-center">
              {/* Accessible Title (required by Radix) */}
              <DialogTitle className="sr-only">
                {isChatDelete ? "Confirm Clear Chat" : "Confirm Delete"}
              </DialogTitle>

              {/* Visible heading */}
              <h2 className="text-lg font-semibold mb-2">
                {isChatDelete ? "Confirm Clear Chat" : "Confirm Delete"}
              </h2>

              <p className="text-muted-foreground text-sm mb-6">
                {isChatDelete
                  ? "Are you sure you want to clear all chat messages ?"
                  : "Are you sure you want to delete this document ?"}
              </p>

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete}>
                  {isChatDelete ? "Clear" : "Delete"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm text-center">
          {dialogStatus === "loading" && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              {/* <p className="text-muted-foreground">Loading...</p> */}
            </div>
          )}

          {dialogStatus === "success" && (
            <div className="py-6">
              <h2 className="text-lg font-semibold text-green-600 mb-2">
                {dialogMessageTitle || "Action Successful"}
              </h2>
              <p className="text-muted-foreground text-sm mb-4">{dialogMessage}</p>
              <Button onClick={() => setDialogOpen(false)} className="w-full">
                Close
              </Button>
            </div>
          )}

          {dialogStatus === "error" && (
            <div className="py-6">
              <h2 className="text-lg font-semibold text-red-600 mb-2">
                {dialogMessageTitle || "Action Failed"}
              </h2>
              <p className="text-muted-foreground text-sm mb-4">{dialogMessage}</p>
              <Button
                variant="destructive"
                onClick={() => setDialogOpen(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      

      <Dialog open={practiceDialogOpen} onOpenChange={setPracticeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle>Edit Practice Details</DialogTitle>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Practice Name</label>
              <Input
                value={practiceName}
                onChange={(e) => setPracticeName(e.target.value)}
                placeholder="Enter practice name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={practiceDescription}
                onChange={(e) => setPracticeDescription(e.target.value)}
                placeholder="Describe this practice"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setPracticeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const updatedPractices = practiceToAdd.map((p) => ({
                  ...p,
                  name: practiceName,
                  description: practiceDescription,
                }));
                handleAddPractice(updatedPractices);
                setPracticeDialogOpen(false);
              }}
            >
              Save Practice
            </Button>
          </div>
        </DialogContent>
      </Dialog>


    </div>
  );
}
